import type {
	CommandConstructor,
	CommandProperties,
	SerializableValue,
} from '@atlassian/rovo-content-bridge-api/command';
import { createCommand } from '@atlassian/rovo-content-bridge-api/command-creator';

import type { NativeEmbedChildCommand, NativeEmbedCommand } from '../../../common/types';
import type { NativeEmbedFeature } from '../../../common/types/features/main';

export interface NativeEmbedCommandParams extends CommandProperties, NativeEmbedCommand {}

export interface NativeEmbedChildCommandParams extends CommandProperties, NativeEmbedChildCommand {}

export const NativeEmbedBridgeCommand: CommandConstructor<
	NativeEmbedCommandParams,
	SerializableValue
> = createCommand<NativeEmbedCommandParams>('native-embed-command');

export const NativeEmbedChildBridgeCommand: CommandConstructor<
	NativeEmbedChildCommandParams,
	SerializableValue
> = createCommand<NativeEmbedChildCommandParams>('native-embed-child-command');

/**
 * Command params for the feature handshake command.
 */
export interface NativeEmbedFeatureHandshakeCommandParams extends CommandProperties {
	features: NativeEmbedFeature[];
}

export const NativeEmbedFeatureHandshakeCommand: CommandConstructor<
	NativeEmbedFeatureHandshakeCommandParams,
	SerializableValue
> = createCommand<NativeEmbedFeatureHandshakeCommandParams>(
	'native-embed-feature-handshake-command',
);

/**
 * Command params for selected-change events (parent → child).
 * Emitted when the embed's selection state changes.
 */
export interface NativeEmbedSelectedChangeCommandParams extends CommandProperties {
	selected: boolean;
}

export const NativeEmbedSelectedChangeCommand: CommandConstructor<
	NativeEmbedSelectedChangeCommandParams,
	SerializableValue
> = createCommand<NativeEmbedSelectedChangeCommandParams>('native-embed-selected-change-command');

/**
 * View mode for the embed container (parent → child).
 */
export type NativeEmbedViewMode = 'regular' | 'maximize';

/**
 * Command params for view-mode-change events (parent → child).
 * Emitted when the embed's view mode changes (e.g. full screen / maximized vs regular).
 */
export interface NativeEmbedViewModeChangeCommandParams extends CommandProperties {
	viewMode: NativeEmbedViewMode;
}

export const NativeEmbedViewModeChangeCommand: CommandConstructor<
	NativeEmbedViewModeChangeCommandParams,
	SerializableValue
> = createCommand<NativeEmbedViewModeChangeCommandParams>('native-embed-view-mode-change-command');

/**
 * Command params for header-visibility-change events (parent → child).
 * Emitted when the host native embed controls/chrome visibility changes.
 */
export type NativeEmbedHeaderVisibilityState = 'visible' | 'not-visible';

export interface NativeEmbedHeaderVisibilityChangeCommandParams extends CommandProperties {
	state: NativeEmbedHeaderVisibilityState;
}

export const NativeEmbedHeaderVisibilityChangeCommand: CommandConstructor<
	NativeEmbedHeaderVisibilityChangeCommandParams,
	SerializableValue
> = createCommand<NativeEmbedHeaderVisibilityChangeCommandParams>(
	'native-embed-header-visibility-change-command',
);

/**
 * Command params for request-delete events forwarded from child to host
 * when request-delete is enabled.
 */
export interface NativeEmbedRequestDeleteCommandParams extends CommandProperties {}

export const NativeEmbedRequestDeleteCommand: CommandConstructor<
	NativeEmbedRequestDeleteCommandParams,
	SerializableValue
> = createCommand<NativeEmbedRequestDeleteCommandParams>('native-embed-request-delete-command');

/**
 * Command params for click events forwarded from child to host
 * when notify-host-click is enabled.
 */
export interface NativeEmbedClickEventCommandParams extends CommandProperties {}

export const NativeEmbedClickEventCommand: CommandConstructor<
	NativeEmbedClickEventCommandParams,
	SerializableValue
> = createCommand<NativeEmbedClickEventCommandParams>('native-embed-click-event-command');

/**
 * Command params for always-show-title-change events (parent → child).
 * Emitted when the user toggles the always-show-title option.
 */
export interface NativeEmbedAlwaysShowTitleChangeCommandParams extends CommandProperties {
	alwaysShowTitle: boolean;
}

export const NativeEmbedAlwaysShowTitleChangeCommand: CommandConstructor<
	NativeEmbedAlwaysShowTitleChangeCommandParams,
	SerializableValue
> = createCommand<NativeEmbedAlwaysShowTitleChangeCommandParams>(
	'native-embed-always-show-title-change-command',
);

/**
 * Command params for toggle-full-screen requests forwarded from child to host
 * when toggle-full-screen is enabled.
 */
export interface NativeEmbedToggleFullScreenCommandParams extends CommandProperties {}

export const NativeEmbedToggleFullScreenCommand: CommandConstructor<
	NativeEmbedToggleFullScreenCommandParams,
	SerializableValue
> = createCommand<NativeEmbedToggleFullScreenCommandParams>(
	'native-embed-toggle-full-screen-command',
);

export {
	NativeEmbedRequestResizeCommand,
	type NativeEmbedRequestResizeCommandParams,
} from '@atlassian/rovo-content-bridge-api-commands/commands/native-embed-request-resize-command';

/**
 * Command params for row-boundary updates pushed from child → host.
 *
 * `boundaries` is an ascending list of Y positions (in child-iframe content
 * pixels) marking the top edge of each row plus the bottom edge of the last
 * row, i.e. N+1 entries for N rows. Sent reactively whenever the child's row
 * layout changes (only for database native embeds).
 */
/**
 * Command params for the content-ready signal pushed from child → host.
 *
 * The child sends this once its content is fully rendered and visible to the
 * user. The host uses it to dismiss its own loading overlay, preventing a
 * flash of the child's internal loading state between the iframe `load` event
 * and the child's first painted frame.
 */
export interface NativeEmbedContentReadyCommandParams extends CommandProperties {}

export const NativeEmbedContentReadyCommand: CommandConstructor<
	NativeEmbedContentReadyCommandParams,
	SerializableValue
> = createCommand<NativeEmbedContentReadyCommandParams>('native-embed-content-ready-command');

/**
 * A single editable-text "slot" and its value for a given remix version.
 * `slot` is the malibu `dataSourceId` (`<componentId>.<propertyPath>`).
 */
export type RemixSlotValue = {
	slot: string;
	value: string;
};

export type RemixVersionTransitionTrigger =
	| 'undo'
	| 'redo'
	| 'collab'
	| 'edit_echo'
	| 'host_version_change';

export type RemixSnapshotSource = 'session' | 'durable' | 'unavailable';

export type VersionApplyFailureReason =
	| 'bridge_send_failed'
	| 'inner_frame_missing'
	| 'invalid_payload'
	| 'apply_timeout'
	| 'slot_apply_failed';

export type NativeEmbedSetRemixVersionCommandResult = {
	applyMode: 'reload_free';
	changedSlotCount?: number;
	durationMs: number;
	outcome: 'success' | 'failure' | 'abort' | 'superseded';
	reason?: VersionApplyFailureReason;
	slotCount?: number;
};

export type NativeEmbedSetRemixVersionCommandContext = {
	applyId?: string;
	snapshotSource?: RemixSnapshotSource;
	trigger?: RemixVersionTransitionTrigger;
};

/**
 * Command params for set-remix-version (parent → child).
 *
 * Sent when the host's stored MAUI (remix) app/version id changes — e.g. after
 * an undo/redo of a version edit. The child swaps the rendered version in place
 * without reloading the outer (host-managed) iframe. Part of the MAUI undo/redo
 * (`cc_maui_editing_experience` experiment).
 *
 * `slots` carries the target version's stored editable-text values so the child
 * can push them straight into malibu's reactive store (reload-free, flash-free)
 * instead of re-pointing the inner iframe. Session-bound; may be empty/omitted
 * for versions with no recorded edits.
 */
export interface NativeEmbedSetRemixVersionCommandParams extends CommandProperties {
	applyId?: string;
	revisionId?: string;
	slots?: RemixSlotValue[];
	snapshotSource?: RemixSnapshotSource;
	trigger?: RemixVersionTransitionTrigger;
	versionId: string;
}

export const NativeEmbedSetRemixVersionCommand: CommandConstructor<
	NativeEmbedSetRemixVersionCommandParams,
	NativeEmbedSetRemixVersionCommandResult
> = createCommand<NativeEmbedSetRemixVersionCommandParams, NativeEmbedSetRemixVersionCommandResult>(
	'native-embed-set-remix-version-command',
);

/**
 * Command params for a created remix version (child → host).
 *
 * Sent by the embedded MAUI app after an edit creates a new version. The host
 * links the versionId to the editor history step created for revisionId.
 */
export interface NativeEmbedRemixVersionCreatedCommandParams extends CommandProperties {
	revisionId?: string;
	versionId: string;
}

export const NativeEmbedRemixVersionCreatedCommand: CommandConstructor<
	NativeEmbedRemixVersionCreatedCommandParams,
	SerializableValue
> = createCommand<NativeEmbedRemixVersionCreatedCommandParams>(
	'native-embed-remix-new-version-command',
);

export interface NativeEmbedRemixNewVersionCommandParams extends CommandProperties {
	previousValue?: string;
	previousVersionId?: string;
	revisionId?: string;
	slot?: string;
	value?: string;
	versionId: string;
}

export const NativeEmbedRemixNewVersionCommand: CommandConstructor<
	NativeEmbedRemixNewVersionCommandParams,
	SerializableValue
> = createCommand<NativeEmbedRemixNewVersionCommandParams>(
	'native-embed-remix-new-version-command',
);

export type FlushRemixEditsResult =
	| {
			outcome: 'success';
			revisionId?: string;
			slots: Array<{ slot: string; value: string }>;
			versionId: string;
	  }
	| { outcome: 'failure' };

export const NativeEmbedFlushRemixEditsCommand: CommandConstructor<
	CommandProperties,
	FlushRemixEditsResult
> = createCommand<CommandProperties, FlushRemixEditsResult>(
	'native-embed-flush-remix-edits-command',
);

export interface NativeEmbedRemixEditCommandParams extends CommandProperties {
	previousValue: string;
	revisionId: string;
	slot: string;
	value: string;
}

export const NativeEmbedRemixEditCommand: CommandConstructor<
	NativeEmbedRemixEditCommandParams,
	SerializableValue
> = createCommand<NativeEmbedRemixEditCommandParams>('native-embed-remix-edit-command');

/**
 * Command params for editor-history (child → host).
 *
 * Sent when the user presses Cmd/Ctrl+Z(+Shift) inside the embedded app's
 * editable text. The host editor is the single undo/redo authority (it drives
 * the remix version pointer), so the child suppresses the browser's native
 * contentEditable undo and forwards the intent here. Part of the MAUI undo/redo
 * (`cc_maui_editing_experience` experiment).
 */
export interface NativeEmbedEditorHistoryCommandParams extends CommandProperties {
	action: 'undo' | 'redo';
}

export const NativeEmbedEditorHistoryCommand: CommandConstructor<
	NativeEmbedEditorHistoryCommandParams,
	SerializableValue
> = createCommand<NativeEmbedEditorHistoryCommandParams>('native-embed-editor-history-command');
